'use client';

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated.
 * Supports user-type gates and RBAC v2 permission gating.
 *
 * Admin routes (/admin/*) redirect to /admin/login (email/password + 2FA)
 * Customer routes redirect to /customer/login (magic-link entry).
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth/AuthContext';
import { UserType } from '@/types/referral';
import { usePermission } from '@/hooks/usePermission';
import { isAdminSignOutRedirectInFlight } from '@/lib/auth/adminLogout';
interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require specific user type (global_admin = Penny Admin, org_admin = Client Admin) */
  requiredUserType?: UserType;
  /** Only allow admin user types (global_admin or org_admin) - blocks customers */
  adminOnly?: boolean;
  /** Only allow customer user type - blocks admins */
  customerOnly?: boolean;
  /** Force redirect to specific login page */
  loginRedirect?: 'admin' | 'customer';
  /**
   * RBAC v2 permission slug to gate access on (additive — coexists with the
   * existing user-type props). Evaluated AFTER `adminOnly`, `customerOnly`
   * and `requiredUserType`, so any of those denies still short-circuits
   * before the permission probe runs.
   *
   * To restrict a page to Penny Admins only, pass a slug that is granted to
   * no non-global_admin role (eg `global_settings.view`,
   * `billing.manage_platform`). The `is_global_admin` short-circuit in
   * `user_has_permission` returns TRUE for Penny Admins on every slug, so
   * such pages remain accessible to platform staff.
   */
  requiredPermission?: string;
  /**
   * Organisation to evaluate `requiredPermission` against. Defaults to
   * `currentOrganization?.id`.
   *
   * Org-less carve-out: when this prop is NOT supplied AND the user has no
   * `currentOrganization` (org-less Penny Admin sessions), the permission
   * probe is skipped and access falls back to `isPlatformAdmin`.
   * This preserves today's behaviour for platform-wide pages whose access
   * list is "Penny Admins" rather than a per-org RBAC permission.
   */
  requiredPermissionOrgId?: string;
  /**
   * Restrict the page to one side of the organisation hierarchy (decided by
   * `currentOrganization.parent_org_id`):
   *   - `'parent'` — only when the selected org is a root parent
   *     (`parent_org_id IS NULL`). Used for surfaces that belong to the
   *     parent org (eg Team, Roles & Permissions).
   *   - `'child'` — only when the selected org is a child (`parent_org_id`
   *     is set). Used for surfaces that operate on the child workspace
   *     (eg Customers).
   *
   * The gate fires regardless of user type — including Penny Admins —
   * because the rule is contextual to the org the user has switched into.
   * No-ops when no organisation is currently selected.
   */
  requiredOrgScope?: 'parent' | 'child';
}
export function ProtectedRoute(t0) {
  const $ = _c(25);
  const {
    children,
    requiredUserType,
    adminOnly,
    customerOnly,
    loginRedirect,
    requiredPermission,
    requiredPermissionOrgId,
    requiredOrgScope
  } = t0;
  const {
    user,
    loading,
    initialising,
    currentOrganization,
    userType,
    isPlatformAdmin,
    isAdminUser
  } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const explicitOrgIdProvided = requiredPermissionOrgId !== undefined;
  const probeOrgId = explicitOrgIdProvided ? requiredPermissionOrgId : currentOrganization?.id ?? null;
  const orgLessCarveOut = !!requiredPermission && !explicitOrgIdProvided && !currentOrganization?.id;
  const probeSlug = requiredPermission && !orgLessCarveOut ? requiredPermission : null;
  const permissionResult = usePermission(probeSlug, probeOrgId ?? null);
  let t1;
  if ($[0] !== initialising || $[1] !== loginRedirect || $[2] !== pathname || $[3] !== router || $[4] !== user) {
    t1 = () => {
      if (!initialising && !user) {
        if (isAdminSignOutRedirectInFlight()) {
          return;
        }
        const isAdminRoute = pathname?.startsWith("/admin");
        const redirectType = loginRedirect || (isAdminRoute ? "admin" : "customer");
        if (redirectType === "admin") {
          router.push("/admin/login");
        } else {
          router.push("/customer/login");
        }
      }
    };
    $[0] = initialising;
    $[1] = loginRedirect;
    $[2] = pathname;
    $[3] = router;
    $[4] = user;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = user?.id;
  let t3;
  if ($[6] !== initialising || $[7] !== loginRedirect || $[8] !== pathname || $[9] !== router || $[10] !== t2) {
    t3 = [t2, initialising, router, pathname, loginRedirect];
    $[6] = initialising;
    $[7] = loginRedirect;
    $[8] = pathname;
    $[9] = router;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t1, t3);
  const [resolvedForUserId, setResolvedForUserId] = useState(null);
  if (user && !loading && resolvedForUserId !== user.id) {
    setResolvedForUserId(user.id);
  }
  const authDataResolved = !!user && resolvedForUserId === user.id;
  if (initialising || loading && !authDataResolved) {
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto" /><p className="mt-4 text-muted-foreground">Loading...</p></div></div>;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (!user) {
    return null;
  }
  if (adminOnly && !isAdminUser) {
    let t4;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">This page is only accessible to administrators.</p></div></div>;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (customerOnly && isAdminUser) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">This page is only accessible to customers.</p></div></div>;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  if (requiredUserType && userType !== requiredUserType) {
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">You don't have the required permissions to access this page.</p></div></div>;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  if (!currentOrganization && !isPlatformAdmin && !customerOnly) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">No Organization Selected</h2><p className="text-muted-foreground">You need to be part of an organization to access this page.</p></div></div>;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  if (requiredOrgScope && currentOrganization) {
    const isChildOrg = !!currentOrganization.parent_org_id;
    const orgIsParent = !isChildOrg;
    const denied = requiredOrgScope === "parent" && !orgIsParent || requiredOrgScope === "child" && !isChildOrg;
    if (denied) {
      const targetLabel = requiredOrgScope === "parent" ? "parent" : "child";
      let t4;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <h2 className="text-2xl font-bold mb-2">Not available for this organisation</h2>;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      let t5;
      if ($[18] !== targetLabel) {
        t5 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md">{t4}<p className="text-muted-foreground">Switch to a {targetLabel} organisation to access this page.</p></div></div>;
        $[18] = targetLabel;
        $[19] = t5;
      } else {
        t5 = $[19];
      }
      return t5;
    }
  }
  if (requiredPermission) {
    if (orgLessCarveOut) {
      if (!isPlatformAdmin) {
        let t4;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">You don't have permission to access this page.</p></div></div>;
          $[20] = t4;
        } else {
          t4 = $[20];
        }
        return t4;
      }
    } else {
      if (permissionResult.loading) {
        let t4;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto" /><p className="mt-4 text-muted-foreground">Loading...</p></div></div>;
          $[21] = t4;
        } else {
          t4 = $[21];
        }
        return t4;
      }
      if (!permissionResult.allowed) {
        let t4;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className="min-h-screen flex items-center justify-center"><div className="text-center max-w-md"><h2 className="text-2xl font-bold mb-2">Access Denied</h2><p className="text-muted-foreground">You don't have permission to access this page.</p></div></div>;
          $[22] = t4;
        } else {
          t4 = $[22];
        }
        return t4;
      }
    }
  }
  let t4;
  if ($[23] !== children) {
    t4 = <>{children}</>;
    $[23] = children;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}